import { AMBER_D, GREEN, MUTED, ORANGE, RED } from '../styles/tokens';

/* Outils revendeur (Pro/Concession) — helpers partagés cartes + page détail. */

/* Jours en ligne depuis first_seen_at (null si inconnu). */
export function daysOnline(firstSeenAt) {
  if (!firstSeenAt) return null;
  const first = new Date(firstSeenAt);
  if (isNaN(first.getTime())) return null;
  const days = Math.floor((Date.now() - first.getTime()) / 86400000);
  return days < 0 ? 0 : days;
}

/* Couleur du badge durée : vert <7j, orange 7-30j, rouge >30j (vendeur motivé). */
export function daysOnlineColor(days) {
  if (days == null) return MUTED;
  if (days < 7) return GREEN;
  if (days <= 30) return ORANGE;
  return RED;
}

export function daysOnlineLabel(days) {
  if (days == null) return '';
  if (days === 0) return "En ligne aujourd'hui";
  return `En ligne depuis ${days} j`;
}

/* ── Fiabilité de la marge ────────────────────────────────────────────────
   Renseignée par le backend (margin_reliability) à partir du pool de
   comparables réellement utilisé : nombre de comparables, coefficient de
   variation et ratio max/min. Trois états affichables :

     SOLIDE  🟢  pool serré, estimation la plus sûre
     CORRECT 🟡  pool acceptable
     absente     marge NON déterminée → ne rien afficher du tout

   Le dernier cas est le plus important : c'est ce qui empêche de remontrer
   les marges fantaisistes issues de comparables hétérogènes (mélange de
   motorisations et de générations).

   ⚠️ EXPLORATOIRE RETIRÉ DE L'AFFICHAGE le 2026-08-16. Sur les véhicules
   anciens, ce palier sortait des prix marché grotesques (VW Vento 1996 à
   274 000 km « marché 14 788 € », Citroën ZX 1993 « marché 10 990 € ») : les
   seuils relâchés laissaient passer des pools inter-générations. Il reste
   calculé et stocké en base pour le diagnostic, mais n'est plus une clé de
   cette table — `marginReliability()` renvoie donc null pour ces annonces, et
   TOUS les affichages (carte, badge, valeur géante, page détail) sont éteints
   par les gardes `reliability &&` existantes. L'API ne les sert plus non plus
   (cf. PUBLIC_MARGIN_RELIABILITIES côté backend) : double verrou volontaire. */
export const MARGIN_RELIABILITY = {
  SOLIDE:  { emoji: '🟢', label: 'Estimation solide',   short: 'Solide',  color: GREEN },
  // Était en ORANGE : un palier qualifie un CHIFFRE D'ARGENT, et l'orange est
  // l'accent d'enquête (cf. styles/money.js). Ambre foncé, lisible sur crème.
  CORRECT: { emoji: '🟡', label: 'Estimation correcte', short: 'Correct', color: AMBER_D },
};

export function marginReliability(listing) {
  return MARGIN_RELIABILITY[listing?.margin_reliability] || null;
}

/* ── Fiabilité de la COTE, à ne pas confondre avec celle de la MARGE ───────
   `margin_reliability` qualifie une marge de revente, et le backend ne
   l'écrit QUE si la marge est positive. S'en servir pour décider d'afficher
   un prix marché, c'est taire la cote de toutes les annonces au-dessus du
   marché — 3 109 annonces à palier public le 2026-09-09, dont pas une seule
   n'était affichable.

   `market_price_reliability` (migration 031) qualifie la COTE elle-même et
   est renseigné sur 100 % des cotes. C'est lui qu'il faut lire pour afficher
   un prix marché ou un écart. Le backend applique la même séparation
   (cf. margin_display.is_public_market_price). */
export function marketReliability(listing) {
  return MARGIN_RELIABILITY[listing?.market_price_reliability] || null;
}

/* L'écart au marché, du point de vue de l'acheteur : positif = sous le
   marché. Rendu null tant que la cote n'est pas affichable, pour qu'aucun
   appelant ne puisse le calculer « à la main » sur un chiffre verrouillé. */
export function marketGap(listing) {
  if (!marketReliability(listing)) return null;
  const cote = listing?.estimated_market_price;
  const prix = listing?.price;
  if (!cote || !prix) return null;
  return { cote, prix, eur: Math.round(cote - prix), pct: Math.round((cote - prix) / cote * 1000) / 10 };
}

/* Une valeur en euros (économie / marge / bénéfice) ne doit JAMAIS être
   affichée sans indicateur de fiabilité — c'est la règle qui protège la
   crédibilité du produit auprès des revendeurs. */
export function hasReliableMargin(listing) {
  return Boolean(marginReliability(listing));
}

/* ── Véhicule HS / non roulant / pour pièces ──────────────────────────────
   Renseigné par le backend (`is_non_runner`, cf. vehicle_state.is_non_runner).
   Ces annonces ne sont JAMAIS servies au plan Particulier — elles sont
   masquées côté API. Là où elles apparaissent (Achat-Revente, Garage Pass),
   elles portent un badge explicite ET n'affichent AUCUNE marge : le pool de
   comparables ne contient que des véhicules roulants, donc l'écart au prix
   marché n'est pas un bénéfice mais le coût des réparations. Annoncer un
   pourcentage ici serait présenter une facture comme une affaire. */
export const NON_RUNNER_BADGE = {
  label: 'HS / pour pièces',
  color: RED,
  bg: 'rgba(179,38,30,0.10)',
};

export function isNonRunner(listing) {
  return Boolean(listing?.is_non_runner);
}

/* Libellé à afficher à la place de la marge sur un véhicule HS. */
export const NON_RUNNER_MARGIN_LABEL = 'Marge non calculable';
export const NON_RUNNER_MARGIN_HINT =
  "La cote d'un véhicule roulant ne s'applique pas à un véhicule HS.";

/* ── Images d'annonces ────────────────────────────────────────────────────
   Les photos sont en HOTLINK : on stocke l'URL, le fichier reste chez la
   source. Deux conséquences à traiter partout où l'on affiche une photo :

   1. `referrerPolicy="no-referrer"` est OBLIGATOIRE sur chaque <img>.
      L'ancien réglage `no-referrer-when-downgrade` envoie quand même le
      Referer en https -> https, et les CDN d'images (Leboncoin en tête) le
      refusent : la photo existe, l'annonce l'affiche, et chez nous elle
      tombait en « Photo indisponible ».

   2. Une image peut malgré tout échouer (annonce retirée, fichier purgé).
      `onImageError` neutralise la balise SANS la retirer du flux : `src`
      est enlevé (donc pas d'icône « image cassée » du navigateur) et la
      boîte garde ses dimensions avec un fond neutre. `display:'none'`
      faisait sauter la mise en page des vignettes. */
export function onImageError(placeholderColor = '#EDF3FA') {
  return e => {
    const el = e.currentTarget;
    el.removeAttribute('src');
    el.style.background = placeholderColor;
  };
}

/* Résumé propre d'une annonce (marque, modèle, prix, marge, lien) pour le partage. */
export function buildShareText(listing) {
  const lines = [
    `${[listing.brand, listing.model].filter(Boolean).join(' ') || listing.title || 'Annonce'}`,
    listing.year || listing.mileage
      ? [listing.year, listing.mileage ? `${listing.mileage.toLocaleString('fr-FR')} km` : null].filter(Boolean).join(' · ')
      : null,
    listing.price ? `Prix : ${listing.price.toLocaleString('fr-FR')} €` : null,
    listing.potential_profit > 0 ? `Marge estimée : +${Math.round(listing.potential_profit).toLocaleString('fr-FR')} €` : null,
    listing.url ? `Lien : ${listing.url}` : null,
    '— via ZENKART',
  ];
  return lines.filter(Boolean).join('\n');
}

/* Partage natif si dispo, sinon copie dans le presse-papiers. Retourne 'shared' | 'copied' | 'failed'. */
export async function shareListing(listing) {
  const text = buildShareText(listing);
  if (navigator.share) {
    try {
      await navigator.share({ title: listing.title || 'Annonce ZENKART', text });
      return 'shared';
    } catch (e) {
      if (e && e.name === 'AbortError') return 'failed';
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
